import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  ShieldCheck,
  BookOpen,
  ScrollText,
  Settings,
  Scale,
  Users,
  Inbox,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { FEATURE_ESCALATIONS } from '@/lib/features';
import { useAuth } from '@/hooks/useAuth';

interface NavItem {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>;
  adminOnly?: boolean;
  featureFlag?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { label: 'Contracts', href: '/contracts', icon: FileText },
  {
    label: 'My Escalations',
    href: '/escalations',
    icon: Inbox,
    featureFlag: FEATURE_ESCALATIONS,
  },
  { label: 'Audit Trail', href: '/audit', icon: ScrollText },
  { label: 'Gate Rules', href: '/rules', icon: ShieldCheck, adminOnly: true },
  { label: 'Knowledge Base', href: '/kb', icon: BookOpen, adminOnly: true },
  { label: 'Users', href: '/users', icon: Users, adminOnly: true },
  { label: 'Settings', href: '/settings', icon: Settings, adminOnly: true },
];

interface SidebarProps {
  className?: string;
}

export function Sidebar({ className }: SidebarProps) {
  const { role } = useAuth();
  const isAdmin = role === 'admin';

  const visibleItems = NAV_ITEMS.filter(
    (item) => (!item.adminOnly || isAdmin) && item.featureFlag !== false,
  );

  return (
    <nav
      aria-label="Main navigation"
      className={cn(
        'flex h-full w-64 flex-col gap-1 border-r border-sidebar-border bg-sidebar px-3 py-4',
        className,
      )}
    >
      {/* Brand mark */}
      <div className="mb-6 flex items-center gap-2 px-2">
        <Scale className="h-6 w-6 text-sidebar-primary" aria-hidden="true" />
        <span className="text-lg font-bold text-sidebar-foreground">CMP</span>
      </div>

      <ul role="list" className="flex flex-col gap-1">
        {visibleItems.map((item) => (
          <li key={item.href}>
            <NavLink
              to={item.href}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring focus-visible:ring-offset-1',
                  isActive
                    ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                    : 'text-sidebar-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground',
                )
              }
            >
              <item.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
              {item.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
